import React from 'react';
import { Box, Tooltip } from '@material-ui/core';
import { Lock } from '@material-ui/icons';
import { useSession } from '../useSession';
import { useOrgInfo } from '../use-org-info/use-org-info';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';

/**
 * True in an enterprise sub-account whose parent has locked core structures
 * (programs, roles, tasks, report types, forms, resources, matching rules,
 * screening questions, email content). They stay viewable but not editable.
 */
export const useEnterpriseLock = (): boolean => {
  const { user } = useSession();
  return !!user?.configItems?.vm.enterpriseSubLockStructures;
};

export const ENTERPRISE_LOCK_FALLBACK_PARENT = 'your parent organisation';

export type EnterpriseLockTooltip = (parentName: string) => string;

export const lockedTooltip = (pluralSubject: string): EnterpriseLockTooltip => (parentName) =>
  `View only. ${pluralSubject} are set up by ${parentName}, so changes need to be made there.`;

const defaultTooltip: EnterpriseLockTooltip = (parentName) =>
  `View only. This is set up by ${parentName}, so changes need to be made there.`;

export const EnterpriseLockNotice = ({
  style,
  tooltip = defaultTooltip,
  iconOnly = false,
}: {
  style?: React.CSSProperties;
  tooltip?: EnterpriseLockTooltip;
  iconOnly?: boolean;
}) => {
  const locked = useEnterpriseLock();
  const orgInfo = useOrgInfo();
  const { theme } = useCampfireTheme();
  if (!locked) return null;

  const parentName = orgInfo?.enterpriseParentName || ENTERPRISE_LOCK_FALLBACK_PARENT;
  const color = theme.color.grey.neutral300;

  return (
    <Tooltip title={iconOnly ? `Managed by ${parentName}. View only.` : tooltip(parentName)}>
      <Box
        tabIndex={0}
        aria-label={iconOnly ? `Managed by ${parentName}` : undefined}
        display='inline-flex'
        alignItems='center'
        flexShrink={0}
        style={{
          gap: '6px',
          padding: iconOnly ? '5px' : '4px 8px',
          borderRadius: '4px',
          background: theme.color.grey.neutralBrand100,
          color,
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '16px',
          whiteSpace: 'nowrap',
          cursor: 'default',
          ...style,
        }}
      >
        <Lock style={{ fontSize: '14px', color }} />
        {iconOnly ? null : <span>Managed by {parentName}</span>}
      </Box>
    </Tooltip>
  );
};
